/** Internal/transient markers — never selectable by a human (OP-6713). */
export const EXCLUDED_ROLE_TYPES = ["None", "Invited", "Deleted"];
/**
 * Roles that must be neither visible nor editable to any user, anywhere in the Access
 * Manager UI (role chips, edit selector, role filter) — stronger than EXCLUDED_ROLE_TYPES,
 * which only hides a role from the *selectable* catalogue while still displaying it if
 * assigned. EudonetAdmin is a cross-tenant super-role (see OPUS `RoleAuthorization.IsEudonetAdmin`)
 * that this UI must never surface or let an Access Manager touch. The backend independently
 * guards this role from add/remove on the full-replace roles-update path — the frontend
 * filter here is a UX layer, not the authoritative enforcement.
 */
export const HIDDEN_ROLE_TYPES = ["EudonetAdmin"];
/** A role a human may pick from a catalogue — neither excluded nor hidden. */
export function isSelectableRole(type) {
    return !EXCLUDED_ROLE_TYPES.includes(type) && !HIDDEN_ROLE_TYPES.includes(type);
}
/**
 * Same order as the backend `UserRoleTypePublic` enum values (None=0 … OpptyEditor=31).
 * Used to render role lists (edit selector, filter dropdown) in a stable, predictable
 * order instead of whatever order the BFF happens to return them in.
 */
export const USER_ROLE_TYPE_ORDER = [
    "None",
    "User",
    "AccessManager",
    "EudonetAdmin",
    "Deleted",
    "ClientPaymentManager",
    "ClientPreferenceManager",
    "Invited",
    "OphubReader",
    "OphubEditor",
    "OpinkCampaignViewer",
    "OpinkCampaignManager",
    "OpinkTemplateViewer",
    "OpinkTemplateManager",
    "OpinkAdmin",
    "OpdonReader",
    "OpdonEditor",
    "OprojUser",
    "OpdocReader",
    "OpdocEditor",
    "OpsolReader",
    "OpsolEditor",
    "OpsolReceptionAgent",
    "OpsolSocialWorker",
    "OpsolDivisionManager",
    "OpsolPhoneOperator",
    "OpconfReader",
    "OpconfEditor",
    "OpsearchUser",
    "OpptyReader",
    "OpptyEditor",
];
export const ROLE_MODULE_ORDER = [
    "general",
    "opay",
    "ophub",
    "opink",
    "opdon",
    "oproj",
    "opdoc",
    "opsol",
    "opconf",
    "opsearch",
    "oppty",
];
const ROLE_MODULE_BY_TYPE = {
    None: "general",
    User: "general",
    AccessManager: "general",
    EudonetAdmin: "general",
    Deleted: "general",
    ClientPaymentManager: "opay",
    ClientPreferenceManager: "general",
    Invited: "general",
    OphubReader: "ophub",
    OphubEditor: "ophub",
    OpinkCampaignViewer: "opink",
    OpinkCampaignManager: "opink",
    OpinkTemplateViewer: "opink",
    OpinkTemplateManager: "opink",
    OpinkAdmin: "opink",
    OpdonReader: "opdon",
    OpdonEditor: "opdon",
    OprojUser: "oproj",
    OpdocReader: "opdoc",
    OpdocEditor: "opdoc",
    OpsolReader: "opsol",
    OpsolEditor: "opsol",
    OpsolReceptionAgent: "opsol",
    OpsolSocialWorker: "opsol",
    OpsolDivisionManager: "opsol",
    OpsolPhoneOperator: "opsol",
    OpconfReader: "opconf",
    OpconfEditor: "opconf",
    OpsearchUser: "opsearch",
    OpptyReader: "oppty",
    OpptyEditor: "oppty",
};
/**
 * Builds the read-only role matrix: every role the tenant's catalogue exposes (excluding
 * EXCLUDED_ROLE_TYPES/HIDDEN_ROLE_TYPES), grouped by module, each flagged `owned` against the
 * target user's actual role set. A role absent from `catalogue` (e.g. a module not enabled for
 * this tenant) is skipped — its module group is omitted entirely if that leaves it empty.
 */
export function buildRoleModuleGroups(catalogue, ownedTypes) {
    const owned = new Set(ownedTypes);
    const labelByType = new Map(catalogue.map((role) => [role.type, role.label]));
    const itemsByModule = new Map();
    for (const type of USER_ROLE_TYPE_ORDER) {
        if (!isSelectableRole(type))
            continue;
        const label = labelByType.get(type);
        if (label === undefined)
            continue;
        const moduleKey = ROLE_MODULE_BY_TYPE[type];
        const items = itemsByModule.get(moduleKey) ?? [];
        items.push({ type, label, owned: owned.has(type) });
        itemsByModule.set(moduleKey, items);
    }
    return ROLE_MODULE_ORDER.filter((key) => itemsByModule.has(key)).map((key) => ({
        key,
        items: itemsByModule.get(key),
    }));
}
